import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { getToken, useAuthStore, User } from '@/state/authState';
import { Permission, SUPPORTED_LANGUAGES, type SupportedLanguage } from '@hr-outsourcing/shared';
import { useLoading } from '@/provider/LoadingProvider.tsx';
import {
  AppAbility,
  canUserPerformAction,
  canUserPerformAllActions,
  canUserPerformAnyAction,
  defineAbilityFor,
} from '@/lib/casl';
import { EntityId } from '@/types/common';
import { resetAllStores } from '@/lib/resetStores';
import { updateMyLanguage } from '@/lib/api';
import { MODULE_PAGE_BASE_PATH } from '@/constants/modulePages';
import { isCareersOrJobsPathname } from '@/lib/jobsSite';

// Languages the app can render come from the shared SUPPORTED_LANGUAGES source of truth.
// Unlike the shared normalizer, this returns null for unrecognized values so callers can
// distinguish "no valid saved language" from a concrete fallback.
const normalizeLang = (value?: string | null): SupportedLanguage | null => {
  const short = (value ?? '').trim().toLowerCase().split(/[-_]/)[0];
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(short) ? (short as SupportedLanguage) : null;
};

const isPublicPathname = (pathname: string): boolean =>
  pathname === '/' ||
  pathname === '/landing' ||
  pathname.startsWith('/auth') ||
  pathname.startsWith('/oauth') ||
  pathname.startsWith('/reset-password') ||
  pathname.startsWith('/new-employee') ||
  pathname.startsWith('/register') ||
  pathname.startsWith('/kvkk-approval') ||
  pathname.startsWith('/kvkk-onay') ||
  pathname.startsWith('/verify/') ||
  pathname === '/pricing' ||
  pathname === '/terms-and-conditions' ||
  pathname === '/privacy' ||
  pathname === '/refund' ||
  pathname === '/about' ||
  pathname === '/distance-sales' ||
  pathname.startsWith(`${MODULE_PAGE_BASE_PATH}/`) ||
  pathname.startsWith('/unsubscribe');

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<User | null>;
  loginWithSession: (session: { token: string; user: unknown }) => void;
  logout: (silent?: boolean) => Promise<void>;
  refreshUser: () => Promise<void>;
  loading: boolean;
  isRole: (role: EntityId | EntityId[]) => boolean;
  hasPermission: (permission: Permission) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  ability: AppAbility;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
const AbilityContext = createContext<AppAbility | undefined>(undefined);
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
interface CanProps {
  I: string | string[];
  a: string | string[];
  children: ReactNode;
  not?: boolean;
  passThrough?: boolean;
}
export const Can = ({ I, a, children, not = false, passThrough = false }: CanProps) => {
  const ability = useContext(AbilityContext);
  if (!ability) {
    if (passThrough) {
      return <>{children}</>;
    }
    return null;
  }
  const actions = Array.isArray(I) ? I : [I];
  const subjects = Array.isArray(a) ? a : [a];
  const canPerform = actions.some((action) => subjects.some((subject) => ability.can(action as any, subject as any)));
  const shouldRender = not ? !canPerform : canPerform;
  if (!shouldRender && !passThrough) {
    return null;
  }
  return <>{children}</>;
};
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const {
    user,
    loading,
    login: loginStore,
    loginWithSession: loginWithSessionStore,
    logout: logoutStore,
    initAuth,
    refreshUser,
  } = useAuthStore(
    useShallow((state) => ({
      user: state.user,
      loading: state.loading,
      login: state.login,
      loginWithSession: state.loginWithSession,
      logout: state.logout,
      initAuth: state.initAuth,
      refreshUser: state.refreshUser,
    }))
  );
  const { show, hide } = useLoading();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const login = useCallback(
    async (email: string, password: string) => {
      resetAllStores(queryClient);
      await loginStore(email, password);
      return useAuthStore.getState().user;
    },
    [loginStore, queryClient]
  );
  const loginWithSession = useCallback(
    (session: { token: string; user: unknown }) => {
      resetAllStores(queryClient);
      loginWithSessionStore(session);
    },
    [loginWithSessionStore, queryClient]
  );
  const logout = useCallback(
    async (silent?: boolean) => {
      resetAllStores(queryClient);
      await logoutStore(silent);
    },
    [logoutStore, queryClient]
  );
  useEffect(() => {
    initAuth();
  }, [initAuth]);
  // Sync the signed-in user's language with the app once auth settles.
  //  - Saved & supported language → open the app in it.
  //  - No saved language yet → persist the current app language (browser language if we support
  //    it, else the i18n fallback) so it becomes sticky and drives transactional emails.
  // After this one-time reconciliation, the language only changes via the LanguageSwitcher.
  useEffect(() => {
    if (loading || !user) return;
    const saved = normalizeLang(user.language);
    if (saved) {
      if (normalizeLang(i18n.resolvedLanguage || i18n.language) !== saved) {
        void i18n.changeLanguage(saved);
      }
      return;
    }
    const current = normalizeLang(i18n.resolvedLanguage || i18n.language) ?? 'en';
    useAuthStore.getState().setUser({ ...user, language: current });
    void updateMyLanguage(current).catch(() => {
      /* silent — best-effort first-time persistence; app language already applied */
    });
  }, [loading, user, i18n]);
  useEffect(() => {
    if (loading) return;
    const token = getToken();
    const isPublicPath = isPublicPathname(location.pathname);
    // Candidate area and public job pages have their own session guard — HR auth must not redirect them.
    const isCareersOrJobsPath = isCareersOrJobsPathname(location.pathname);
    if (!token && !isPublicPath && !isCareersOrJobsPath) {
      navigate('/');
    }
  }, [user, location.pathname, loading, navigate]);
  useEffect(() => {
    const isPublicPath = isPublicPathname(location.pathname);
    const isCareersOrJobsPath = isCareersOrJobsPathname(location.pathname);
    const shouldBeLoading = !isPublicPath && !isCareersOrJobsPath && (loading || !user);
    if (shouldBeLoading) {
      show(t('auth.loadingApplication'));
    } else {
      hide();
    }
    return () => {
      hide();
    };
  }, [loading, user, location.pathname, show, hide]);
  const isRole = useCallback(
    (roles: EntityId | EntityId[]): boolean => {
      const rolesToCheck = Array.isArray(roles) ? roles : [roles];
      if (!user?.role?.id) return false;
      return rolesToCheck.includes(user.role.id);
    },
    [user]
  );
  const ability = useMemo(() => defineAbilityFor(user), [user]);
  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      return canUserPerformAction(ability, permission);
    },
    [ability]
  );
  const hasAllPermissions = useCallback(
    (permissions: Permission[]): boolean => {
      if (!user) return false;
      return canUserPerformAllActions(ability, permissions);
    },
    [ability, user]
  );
  const hasAnyPermission = useCallback(
    (permissions: Permission[]): boolean => {
      if (!user) return false;
      return canUserPerformAnyAction(ability, permissions);
    },
    [ability, user]
  );
  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      loginWithSession,
      logout,
      refreshUser,
      isRole,
      hasPermission,
      hasAllPermissions,
      hasAnyPermission,
      ability,
    }),
    [
      user,
      loading,
      login,
      loginWithSession,
      logout,
      refreshUser,
      isRole,
      hasPermission,
      hasAllPermissions,
      hasAnyPermission,
      ability,
    ]
  );
  const isCareersOrJobsPage = isCareersOrJobsPathname(location.pathname);
  const canRenderChildren =
    isPublicPathname(location.pathname) || isCareersOrJobsPage || (!loading && user);
  if (!canRenderChildren) {
    return null;
  }
  return (
    <AbilityContext.Provider value={ability}>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    </AbilityContext.Provider>
  );
};
